import { useState, useRef, useEffect } from 'react';

export function useDebouncedValue<T>(
  value: T,
  delay: number,
  maxWait: number,
): T {
  const [debounced, setDebounced] = useState(value);
  const lastUpdateRef = useRef(Date.now());
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateRef.current;

    if (timeSinceLastUpdate >= maxWait) {
      // Force update if maxWait exceeded
      setDebounced(value);
      lastUpdateRef.current = now;
    } else {
      // Normal debounce
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setDebounced(value);
        lastUpdateRef.current = Date.now();
      }, delay);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [value, delay, maxWait]);

  return debounced;
}
